import React, { useEffect, useMemo } from 'react'
import dayjs from 'dayjs'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  FormControlLabel,
  Switch,
} from '@mui/material'
import EnhancedTableHead from './EnhancedTableHeader'
import Loading from '../Loading'
import RowRender from './RowRender'
import { OrderEnhancedTableToolbar } from '../../views/Orders/table/EnhancedTableToolbar/OrderEnhancedTableToolbar'
import { CustomerEnhancedTableToolbar } from './EnhancedTableToolbar/customers/CustomerEnhancedTableToolbar'
import { CampaignEnhancedTableToolbar } from './EnhancedTableToolbar/campaigns/CampaignEnhancedTableToolbar'
import { LeadEnhancedTableToolbar } from './EnhancedTableToolbar/leads/leadEnhancedTableToolbar'
import { useDispatch, useSelector } from '../../store/store'
import { removeSelected, setSelected, setType } from '../../store/slices/table'
import { clearFilters } from '../../store/slices/filters/filters'
import { generateDefaultDense, getComparator, stableSort } from '../../utils/tools'

export type TableTypes = 'order' | 'customer' | 'campaign' | 'lead'

interface TableRenderProps {
  data?: unknown[]
  isLoading: boolean
  type: TableTypes
  checkbox?: boolean
  toolbar?: boolean
  pagination?: boolean
  densePadding?: boolean
  defaultOrderBy: string
  emptyPlaceholder?: React.ReactNode
  AnteFilterBtn?: JSX.Element
  page?: number
  rowsPerPage?: number
  setRowsPerPage?: (page: number) => void
  rowsPerPageItemName?: string
  setPage?: (page: number) => void
  isOverview?: boolean
  onRowClick?: (id: string) => void
}

type Order = 'asc' | 'desc'

const handleFilters = (rows, filters) => {
  if (!filters || !filters.length) {
    return rows
  }

  return rows.filter((row) => {
    return filters.every((filter) => {
      if (filter.type === 'date') {
        const rowDate = dayjs(row[filter.id])
        const filterDate = dayjs(filter.value)

        if (filter.operator === 'equals') {
          return rowDate.isSame(filterDate, 'day')
        }

        if (filter.operator === 'after') {
          return rowDate.isAfter(filterDate, 'day')
        }

        if (filter.operator === 'before') {
          return rowDate.isBefore(filterDate, 'day')
        }

        if (filter.operator === 'between') {
          return (
            rowDate.isAfter(dayjs(filter.value[0]), 'day') &&
            rowDate.isBefore(dayjs(filter.value[1]), 'day')
          )
        }
      }

      if (filter.type === 'select') {
        return filter.value.includes(row[filter.id])
      }

      if (filter.type === 'text') {
        if (filter.operator === 'equals') {
          return row[filter.id] === filter.value
        }

        if (filter.operator === 'contains') {
          return row[filter.id]?.toLowerCase().includes(filter.value.toLowerCase())
        }
      }

      return true
    })
  })
}

export default function TableRender({
  data = [],
  isLoading = false,
  type,
  checkbox = false,
  toolbar = false,
  pagination = false,
  densePadding = false,
  defaultOrderBy = '',
  emptyPlaceholder = null,
  isOverview = false,
  page: externalPage,
  rowsPerPage: externalRowsPerPage,
  setPage: externalSetPage,
  setRowsPerPage: externalSetRowsPerPage,
  rowsPerPageItemName: externalRowsPerPageItemName,
  onRowClick,
}: TableRenderProps) {
  const densePaddingItemName = `${type}TableDensePadding`
  const rowsPerPageItemName = externalRowsPerPageItemName || `${type}TableRowsPerPage`
  const defaultRowsPerPage = Number(localStorage.getItem(rowsPerPageItemName)) || 5

  const defaultDense = generateDefaultDense(densePaddingItemName)

  const { selected } = useSelector((state) => state.table)
  const { filters } = useSelector((state) => state.filters)
  const { permissions } = useSelector((state) => state.user)

  const [order, setOrder] = React.useState<Order>('desc')
  const [orderBy, setOrderBy] = React.useState<string>(defaultOrderBy)
  const [rows, setRows] = React.useState<unknown[] | never[]>([])
  const [dense, setDense] = React.useState(isOverview || defaultDense)
  const [page, setPageInternal] = React.useState(0)
  const [rowsPerPage, setRowsPerPageInternal] = React.useState(defaultRowsPerPage)

  // Use external state if provided, otherwise use internal state
  const currentPage = externalPage !== undefined ? externalPage : page
  const currentRowsPerPage = externalRowsPerPage !== undefined ? externalRowsPerPage : rowsPerPage
  const setPageFn = externalSetPage || setPageInternal
  const setRowsPerPageFn = externalSetRowsPerPage || setRowsPerPageInternal

  useEffect(() => {
    if (rows.length <= currentRowsPerPage) {
      setPageFn(0)
    }
  }, [rows, currentRowsPerPage, setPageFn])

  const dispatch = useDispatch()

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows.map((n: any) => n.id)
      dispatch(setSelected(newSelected))
      return
    }

    dispatch(removeSelected())
  }

  const handleChangePage = (_e, newPage: number) => {
    setPageFn(newPage)
  }

  const handleChangeRowsPerPageCB = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPageFn(parseInt(event.target.value, 10))
      setPageFn(0)
      localStorage.setItem(rowsPerPageItemName, String(event.target.value))
    },
    [rowsPerPageItemName, setPageFn, setRowsPerPageFn]
  )

  const handleChangeDenseCB = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDense(event.target.checked)
      localStorage.setItem(densePaddingItemName, String(event.target.checked))
    },
    [densePaddingItemName]
  )

  const isSelected = (id: string) => {
    return selected.indexOf(id) !== -1
  }

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    currentPage > 0 ? Math.max(0, (1 + currentPage) * currentRowsPerPage - rows.length) : 0

  const filteredRows = useMemo(() => handleFilters(rows, filters), [rows, filters])

  const visibleRows = useMemo(
    () =>
      rows &&
      stableSort(filteredRows, getComparator(order, orderBy)).slice(
        currentPage * currentRowsPerPage,
        currentPage * currentRowsPerPage + currentRowsPerPage
      ),
    [rows, filteredRows, order, orderBy, currentPage, currentRowsPerPage]
  )

  useEffect(() => {
    if (data) {
      dispatch(setType(type))
      dispatch(removeSelected()) // It cleans selection on table render

      setRows(data)
    }
  }, [data, dispatch, permissions?.customersDetailsFields, type])

  useEffect(() => {
    return () => {
      dispatch(clearFilters())
    }
  }, [dispatch])

  const hasPermission = permissions?.leadsAndTalents
  !hasPermission && null

  const showToolbar = toolbar && !isOverview

  return (
    <Box sx={{ width: '100%' }}>
      {showToolbar && type === 'order' && <OrderEnhancedTableToolbar />}
      {showToolbar && type === 'customer' && <CustomerEnhancedTableToolbar />}
      {showToolbar && type === 'campaign' && <CampaignEnhancedTableToolbar />}
      {showToolbar && type === 'lead' && <LeadEnhancedTableToolbar />}

      {isLoading ? (
        <Loading />
      ) : (
        <TableContainer sx={{ overflowX: !data.length && emptyPlaceholder ? 'hidden' : null }}>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
          >
            <EnhancedTableHead
              checkbox={checkbox}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows?.length || 0}
            />

            <TableBody>
              <RowRender
                rows={visibleRows}
                isSelected={isSelected}
                checkbox={checkbox}
                customClickHandler={onRowClick}
              />
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
          {!data.length && emptyPlaceholder}
        </TableContainer>
      )}
      {pagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 100, 200]}
          component="div"
          count={rows.length || 0}
          rowsPerPage={currentRowsPerPage}
          page={currentPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPageCB}
        />
      )}
      {densePadding && !isOverview && (
        <FormControlLabel
          control={<Switch checked={dense} onChange={handleChangeDenseCB} />}
          label="Dense padding"
        />
      )}
    </Box>
  )
}
